// frontend/src/hooks/useGeolocation.js
// Custom hook: watches GPS position in real-time

import { useState, useEffect, useCallback } from "react";

const PROXIMITY_RADIUS = parseFloat(process.env.REACT_APP_PROXIMITY_RADIUS) || 7;

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useGeolocation(poles = []) {
  const [userPos, setUserPos]       = useState(null);
  const [distances, setDistances]   = useState({});   // { poleId: meters }
  const [nearPoles, setNearPoles]   = useState({});   // { poleId: true/false }
  const [gpsError, setGpsError]     = useState(null);

  const processPosition = useCallback((lat, lng) => {
    setUserPos({ lat, lng });
    const newDist = {};
    const newNear = {};
    poles.forEach((pole) => {
      const d = haversineDistance(lat, lng, parseFloat(pole.latitude), parseFloat(pole.longitude));
      newDist[pole.id] = d;
      newNear[pole.id] = d <= PROXIMITY_RADIUS;
    });
    setDistances(newDist);
    setNearPoles(newNear);
  }, [poles]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError("Geolocation is not supported by this browser.");
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsError(null);
        processPosition(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => setGpsError(`GPS Error: ${err.message}`),
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [processPosition]);

  return { userPos, distances, nearPoles, gpsError, haversineDistance };
}
